import type { MouseEvent } from 'react';

/**
 * Single source of truth for the booking link. Change this one value to point
 * every "book a call / consultation" CTA at a new scheduling page.
 */
export const CALENDLY_URL = 'https://calendly.com/automateitng/30min';

const WIDGET_JS = 'https://assets.calendly.com/assets/external/widget.js';
const WIDGET_CSS = 'https://assets.calendly.com/assets/external/widget.css';

interface CalendlyWindow extends Window {
    Calendly?: { initPopupWidget: (opts: { url: string }) => void };
}

let loadPromise: Promise<void> | null = null;

/**
 * Lazily inject the Calendly popup widget assets exactly once. The returned
 * promise resolves when the script is ready and rejects if it fails to load
 * (e.g. blocked network), so callers can fall back to a plain link.
 */
export function loadCalendly(): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();
    if (loadPromise) return loadPromise;

    loadPromise = new Promise<void>((resolve, reject) => {
        if (!document.querySelector(`link[href="${WIDGET_CSS}"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = WIDGET_CSS;
            document.head.appendChild(link);
        }

        if ((window as CalendlyWindow).Calendly) return resolve();

        let script = document.querySelector<HTMLScriptElement>(`script[src="${WIDGET_JS}"]`);
        if (!script) {
            script = document.createElement('script');
            script.src = WIDGET_JS;
            script.async = true;
            document.head.appendChild(script);
        }
        script.addEventListener('load', () => resolve());
        script.addEventListener('error', () => {
            loadPromise = null; // allow a retry on the next interaction
            reject(new Error('Calendly widget failed to load'));
        });
    });

    return loadPromise;
}

/**
 * Opens Calendly in an on-page popup. Attach to an anchor whose `href` points
 * at {@link CALENDLY_URL} so the CTA still works when JS/the widget is blocked:
 * modified clicks and load failures fall through to the native link.
 */
export function openCalendly(e?: MouseEvent<HTMLAnchorElement>): void {
    // Let cmd/ctrl/shift/middle clicks open the href in a new tab natively.
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) return;

    const w = window as CalendlyWindow;
    e?.preventDefault();

    if (w.Calendly) {
        w.Calendly.initPopupWidget({ url: CALENDLY_URL });
        return;
    }

    loadCalendly()
        .then(() => (window as CalendlyWindow).Calendly?.initPopupWidget({ url: CALENDLY_URL }))
        .catch(() => window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer'));
}

/**
 * Spread onto an anchor to turn it into a booking CTA:
 * `<a {...calendlyLinkProps} className="btn-primary">Book a call</a>`
 */
export const calendlyLinkProps = {
    href: CALENDLY_URL,
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: openCalendly,
} as const;
